import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { NotificationType } from '../../models/notification-type';
import { NotificationService } from '../notification/notification.service';

@Injectable()
export class ErrorHandlerService {
    private readonly genericErrorMessage = 'Something went wrong. Please try again later or contact administrator for further assistance.';

    // Array of patterns for matching errors with user-friendly messages
    private errorMessagePatterns: Array<{ pattern: string; message: string }> = [
        {
            pattern: 'Execution Timeout',
            message: 'The request took too long to process. Please try again.',
        },
        {
            pattern: '400',
            message: 'The request could not be understood. Please check your input and try again.',
        },
        {
            pattern: '401',
            message: 'You are not authorized to perform this action. Please log in and try again.',
        },
        {
            pattern: '403',
            message: 'You do not have permission to access this resource.',
        },
        {
            pattern: '404',
            message: 'The requested resource could not be found.',
        },
        {
            pattern: '500',
            message: 'There was an issue processing your request. Please try again later.',
        },
        {
            pattern: '502',
            message: 'The server received an invalid response. Please try again later.',
        },
        {
            pattern: '503',
            message: 'The service is currently unavailable. Please try again later.',
        },
        {
            pattern: '504',
            message: 'The server took too long to respond. Please try again.',
        },
        {
            pattern: 'timeout',
            message: 'It seems your connection is slow. Please retry.',
        },
        {
            pattern: 'network',
            message: 'A network issue occurred. Please check your internet connection.',
        },
        {
            pattern: 'unexpected',
            message: 'An unexpected error occurred. Please try again later.',
        },
    ];

    public constructor(private notificationService: NotificationService) { }

    public getHttpErrorMessage(err: HttpErrorResponse): string {
        const errorBody = err.error;
        let errorMessage = errorBody?.Message || errorBody?.error_description || errorBody?.message || this.genericErrorMessage;

        const customMessage = err.headers.get('X-Custom-Message');
        if (customMessage) {
            errorMessage = customMessage;
        }

        const shouldShowFriendlyMessage = environment.production && ![400, 401, 403, 404].includes(err.status) && !customMessage;
        if (shouldShowFriendlyMessage) {
            errorMessage = this.getFriendlyMessage(errorMessage);
        }

        return errorMessage;
    }

    public logHttpError(err: HttpErrorResponse, message?: string): void {
        const errorMessage = message || this.getHttpErrorMessage(err);
        this.notificationService.notify(
            errorMessage,
            'Error',
            NotificationType.ERROR,
        );

        console.error(err);
    }

    private getFriendlyMessage(error: string): string {
        // Find the first matching pattern
        const matchingPattern = this.errorMessagePatterns.find(({ pattern }) =>
            error.toLowerCase().includes(pattern.toLowerCase()),
        );

        // Return the friendly message or the default message
        return matchingPattern?.message || this.genericErrorMessage;
    }
}
